import type { BlogPost } from '../types';

export const post: BlogPost = {
  "id": "432",
  "slug": "coldest-places-on-earth",
  "title": "The Coldest Places on Earth",
  "excerpt": "From the Antarctic plateau to a Siberian village where the ground stays frozen year-round, here are the coldest places on the planet and what makes them so brutally cold.",
  "date": "2026-06-14",
  "category": "Geography",
  "image": "https://images.unsplash.com/photo-1549752448-38f4d94420f5?ixid=M3w5NjY4NzN8MHwxfHNlYXJjaHwxfHxmcm96ZW4lMjB3aW50ZXIlMjBsYW5kc2NhcGV8ZW58MXwwfHx8MTc4MTQyNTA3NXww&ixlib=rb-4.1.0&w=800&q=80&fit=crop",
  "readTime": "4 min",
  "author": "EarthGuessr Team",
  "content": [
    {
      "type": "paragraph",
      "text": "Some places on Earth get cold enough that exposed skin freezes in under a minute, diesel turns to jelly, and a thrown cup of boiling water vanishes into ice crystals before it hits the ground. These are not freak weather days. For the coldest places on the planet, this is just winter."
    },
    {
      "type": "paragraph",
      "text": "Cold of this scale comes down to three ingredients: high latitude, high elevation, and distance from the ocean. The sea is a giant heat reservoir that moderates temperatures, so the most extreme cold builds up in places that are far inland, far from the equator, and high above sea level. Antarctica checks all three boxes, which is why it dominates this list."
    },
    {
      "type": "heading",
      "text": "Antarctica: the undisputed champion"
    },
    {
      "type": "paragraph",
      "text": "The lowest air temperature ever directly recorded at the surface was minus 89.2 degrees Celsius (minus 128.6 Fahrenheit), measured at Russia's Vostok Station on the East Antarctic Plateau on July 21, 1983. Vostok sits more than 3,400 meters above sea level, deep in the continental interior, where the long polar night and thin, dry air let heat radiate straight out into space."
    },
    {
      "type": "paragraph",
      "text": "Satellites have since found even colder spots. A 2018 study using NASA and USGS satellite data identified pockets along a high ridge of the East Antarctic Plateau where the snow surface dropped to around minus 98 Celsius. There is an important catch: that figure is the temperature of the ice surface itself, not the air above it, so it is not directly comparable to Vostok's official air-temperature record. Either way, the message is the same. Nowhere else on Earth comes close."
    },
    {
      "type": "heading",
      "text": "The coldest inhabited places"
    },
    {
      "type": "paragraph",
      "text": "Antarctica has research stations but no permanent population. The title of coldest place where people actually live belongs to Siberia, and two villages have spent decades arguing over it."
    },
    {
      "type": "list",
      "items": [
        "Oymyakon, Russia: this remote village in the Sakha Republic recorded an official low of about minus 67.7 Celsius in 1933, and is widely called the coldest permanently inhabited place on Earth.",
        "Verkhoyansk, Russia: a few hundred kilometers away, it has logged nearly identical lows and shares the nickname Pole of Cold of the Northern Hemisphere.",
        "Snag, Yukon, Canada: the coldest place ever recorded in North America hit about minus 63 Celsius in 1947.",
        "Eismitte and Klinck, Greenland: stations on the Greenland ice sheet have dropped below minus 69 Celsius, the coldest reliably measured outside Antarctica."
      ]
    },
    {
      "type": "paragraph",
      "text": "What links Oymyakon and Verkhoyansk is geography. Both sit in deep river valleys far from any ocean, where dense cold air pools and settles during the long, still Arctic winter. Cars are often left running for days because restarting them in the cold is nearly impossible."
    },
    {
      "type": "heading",
      "text": "Why these places, and not others?"
    },
    {
      "type": "paragraph",
      "text": "The North Pole is actually milder than interior Siberia, because it sits on sea ice floating over a relatively warm ocean. Elevation matters just as much as latitude: air cools as it rises, so high plateaus stay colder than low coasts at the same distance from the equator. Add a cloudless sky and a months-long night, and heat escapes upward with nothing to trap it. That combination is what turns a cold place into a record-breaking one."
    },
    {
      "type": "heading",
      "text": "Life in the deep freeze"
    },
    {
      "type": "paragraph",
      "text": "Extreme cold does more than make a place uncomfortable; it reshapes the entire landscape. In the coldest inhabited regions, the ground itself is permanently frozen, a layer called permafrost that can run hundreds of meters deep. Buildings have to be raised on stilts or insulated pads so their warmth does not melt the frozen soil and sink the foundations. Water pipes are run above ground or heated, and ordinary plumbing simply does not work the way it does elsewhere."
    },
    {
      "type": "paragraph",
      "text": "Everyday life adapts in surprising ways. In Oymyakon, schools may stay open until the temperature drops well past minus 50 Celsius, eyelashes frost over within minutes outdoors, and growing fresh food locally is nearly impossible for much of the year. Wildlife copes too, with thick fur, antifreeze-like compounds in the blood of some animals, and long winter dormancy. The cold is not just a number on a thermometer; it is the organizing force of the whole region."
    },
    {
      "type": "heading",
      "text": "Spotting the cold from above"
    },
    {
      "type": "paragraph",
      "text": "From satellite imagery, the coldest regions are easy to recognize: blinding white ice sheets, frozen rivers threading through dark forest, and a near-total absence of roads or towns. Learning to read those clues is half the fun of guessing where on Earth a satellite image was taken."
    }
  ]
};
